"use client";

import { SDUIOnClickHandler } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/SDUIOnClickHandler/SDUIOnClickHandler";
import {
  ActionLoggingEventParams,
  ComponentLoggingEventParams,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/types";
import { getSlateButtonVariant } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Button/getSlateButtonVariant";
import { SDUIGridHeaderRow } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Grid/__internal__/SDUIGridHeaderRow";
import { SDUIGridRow } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Grid/__internal__/SDUIGridRow";
import { useCurrentTruncationBehaviour } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Grid/__internal__/utils/useGetCurrentTruncationBehaviour";
import { useResponsiveTileWidth } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Grid/__internal__/utils/useResponsiveTileWidth";
import {
  DynamicVariable,
  assignInlineVars,
} from "@faire/deprecated-retailer-visitor-shared/lib/tailwindUtils";
import { getSlateSpacingPixels } from "@faire/retailer-shared/SlateMappings/getSlateSpacingPixels";
import { IAction } from "@faire/web-api--source/indigofair/data/IAction";
import { IButtonElement } from "@faire/web-api--source/indigofair/data/IButtonElement";
import { IGridComponent } from "@faire/web-api--source/indigofair/data/IGridComponent";
import { IGridStyle } from "@faire/web-api--source/indigofair/data/IGridStyle";
import { IImageTile } from "@faire/web-api--source/indigofair/data/IImageTile";
import { Spacing } from "@faire/web-api--source/indigofair/data/Spacing";
import { Button } from "@faire/web/slate/Button";
import { Arrow } from "@faire/web/slate/icons/foundation/Arrow";
import { Column } from "@faire/web/slate/Layout";
import { cn } from "@faire/web/tools/cn";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { useViewportWidth } from "@faire/web/ui/hooks/useViewportWidth";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import chunk from "lodash/chunk";
import React, { useEffect, useMemo, useRef, useState } from "react";

export const AUTO_COLUMN_LAYOUT_GRID_MAX_TILES_PER_ROW = 9;

const varMobileNumColumns: DynamicVariable<"--f-sdui-grid-nc-m"> = {
  variable: "--f-sdui-grid-nc-m",
  className: "grid-cols-(--f-sdui-grid-nc-m)",
};

const varTabletNumColumns: DynamicVariable<"--f-sdui-grid-nc-md"> = {
  variable: "--f-sdui-grid-nc-md",
  className: "md:grid-cols-(--f-sdui-grid-nc-md)",
};

const varDesktopNumColumns: DynamicVariable<"--f-sdui-grid-nc-lg"> = {
  variable: "--f-sdui-grid-nc-lg",
  className: "lg:grid-cols-(--f-sdui-grid-nc-lg)",
};

export const SDUIGridAutoColumnLayoutBase: React.FC<{
  gridComponent: IGridComponent;
  loggingData: ComponentLoggingEventParams;
}> = ({ gridComponent, loggingData }) => {
  const {
    log_tile_batch_view,
    responsive_truncation_behaviour,
    style = IGridStyle.build(),
    title,
    responsive_title,
    header_background_icon,
    responsive_header_background_icon,
    tiles,
    truncation_behaviour = IGridComponent.ITruncationBehaviour.build(),
  } = gridComponent;

  const { column_spacing, row_spacing } = style;
  const columnSpacingPixels = useMemo(
    () => getSlateSpacingPixels(column_spacing ?? Spacing.SPACING_NONE),
    [column_spacing]
  );

  // TODO (FD-199432): This is an anti-pattern and we generally don't want components reaching into
  // their children's contents to retrive information. This information should be moved into the proto.
  const imageTileVariant: keyof typeof IImageTile.ImageTileVariant = useMemo(
    () =>
      tiles.find((tile) => tile.image_tile?.style?.variant)?.image_tile?.style
        ?.variant ?? IImageTile.ImageTileVariant.IMAGE_TILE_VARIANT_UNKNOWN,
    [tiles]
  );

  const viewportWidth = useViewportWidth((width) => width);
  const containerRef = useRef<HTMLDivElement>(null);

  const truncationBehaviour = useCurrentTruncationBehaviour(
    truncation_behaviour,
    responsive_truncation_behaviour
  );

  const [currentNumberOfRows, setCurrentNumberOfRows] = useState<
    number | undefined
  >(
    truncationBehaviour.noTruncation
      ? undefined
      : (truncationBehaviour.clipTrunctation?.max_rows ??
          truncationBehaviour.expandTruncation?.collapsed_max_rows)
  );

  const [currentNumberOfTilesPerRow, setCurrentNumberOfTilesPerRow] = useState<
    number | undefined
  >(undefined);

  const hasHiddenTiles =
    currentNumberOfRows !== undefined &&
    currentNumberOfTilesPerRow !== undefined &&
    tiles.length > currentNumberOfTilesPerRow * currentNumberOfRows;

  const expandButtonLoggingParams = useMemo(() => {
    return ActionLoggingEventParams.build({
      ...loggingData,
      action_instance_identifier: "EXPAND",
    });
  }, [loggingData]);

  const tileChunks = useMemo(() => {
    return chunk(tiles, currentNumberOfTilesPerRow);
  }, [currentNumberOfTilesPerRow, tiles]);

  useEffect(() => {
    setCurrentNumberOfRows(
      truncationBehaviour.noTruncation
        ? undefined
        : (truncationBehaviour.clipTrunctation?.max_rows ??
            truncationBehaviour.expandTruncation?.collapsed_max_rows)
    );
  }, [
    truncationBehaviour.noTruncation,
    truncationBehaviour.clipTrunctation?.max_rows,
    truncationBehaviour.expandTruncation?.collapsed_max_rows,
  ]);

  const responsiveTileWidth = useResponsiveTileWidth(imageTileVariant);
  const tileWidth = useResponsiveValue(responsiveTileWidth);

  useEffect(() => {
    const containerWidth = containerRef.current?.getBoundingClientRect().width;

    if (!containerWidth) {
      setCurrentNumberOfTilesPerRow(AUTO_COLUMN_LAYOUT_GRID_MAX_TILES_PER_ROW);
    } else if (containerWidth && tileWidth) {
      // the equation used to get the number of tiles that fit in the component here is
      // containerWidth = (tileWidth * numTiles) + [columnSpacingPixels * (numTiles - 1)]
      const tilesPerRow = Math.min(
        Math.floor(
          (containerWidth + columnSpacingPixels) /
            (tileWidth + columnSpacingPixels)
        ),
        AUTO_COLUMN_LAYOUT_GRID_MAX_TILES_PER_ROW
      );
      setCurrentNumberOfTilesPerRow(tilesPerRow);
    }
  }, [viewportWidth, imageTileVariant, columnSpacingPixels, tileWidth]);

  const vars = {
    [varDesktopNumColumns.variable]: responsiveTileWidth.desktopAndAbove
      ? `repeat(auto-fill, ${responsiveTileWidth.desktopAndAbove}px)`
      : `repeat(auto-fit, min-content)`,
    [varTabletNumColumns.variable]: responsiveTileWidth.tabletAndAbove
      ? `repeat(auto-fill, ${responsiveTileWidth.tabletAndAbove}px)`
      : `repeat(auto-fit, min-content)`,
    [varMobileNumColumns.variable]: responsiveTileWidth.mobileAndAbove
      ? `repeat(auto-fill, ${responsiveTileWidth.mobileAndAbove}px)`
      : `repeat(auto-fit, min-content)`,
  };

  return (
    <Column className="w-full">
      <SDUIGridHeaderRow
        title={title}
        responsiveTitle={responsive_title}
        backgroundIcon={header_background_icon}
        responsiveBackgroundIcon={responsive_header_background_icon}
        loggingData={loggingData}
      />

      {tileChunks.map((tiles, index) => {
        if (currentNumberOfRows !== undefined && index >= currentNumberOfRows) {
          return null;
        }
        return (
          <SDUIGridRow
            key={tiles[0]?.logging_data?.instance_identifier ?? index}
            tiles={tiles}
            loggingData={loggingData}
            className={cn(
              "grid justify-between",
              varMobileNumColumns.className,
              varTabletNumColumns.className,
              varDesktopNumColumns.className
            )}
            componentStyle={{
              ...assignInlineVars(vars),
              gap: `${getSlateSpacingPixels(row_spacing ?? Spacing.SPACING_NONE)}px ${getSlateSpacingPixels(column_spacing ?? Spacing.SPACING_NONE)}px`,
              marginTop: `${getSlateSpacingPixels(index === 0 || !row_spacing ? Spacing.SPACING_NONE : row_spacing)}px`,
            }}
            containerRef={index === 0 ? containerRef : undefined}
            logTileBatchView={log_tile_batch_view}
          />
        );
      })}
      {truncationBehaviour.expandTruncation && hasHiddenTiles ? (
        <ShowMoreToggleButton
          variant={truncationBehaviour.expandTruncation.variant}
          numHiddenTiles={
            tiles.length - currentNumberOfTilesPerRow * currentNumberOfRows
          }
          onClick={() => setCurrentNumberOfRows(undefined)}
          isGridExpanded={currentNumberOfRows === undefined}
          loggingEventParams={expandButtonLoggingParams}
          clickActions={truncationBehaviour.expandTruncation.on_click}
        />
      ) : null}
    </Column>
  );
};

const ShowMoreToggleButton: React.FC<{
  isGridExpanded?: boolean;
  variant?: keyof typeof IButtonElement.ButtonVariant;
  numHiddenTiles: number;
  onClick: () => void;
  loggingEventParams: ActionLoggingEventParams;
  clickActions?: IAction[];
}> = ({
  isGridExpanded,
  variant = IButtonElement.ButtonVariant.PRIMARY,
  numHiddenTiles,
  onClick,
  loggingEventParams,
  clickActions = [],
}) => {
  return (
    <SDUIOnClickHandler
      className="mt-3x mx-auto mb-0 w-max"
      onClickActions={clickActions}
      loggingData={loggingEventParams}
    >
      <Button variant={getSlateButtonVariant(variant)} onClick={onClick}>
        {isGridExpanded ? null : (
          <>
            <StrictLocalMsg
              id="4cwiCv"
              defaultMessage="{tileNumber, plural, one {Show # more} other {Show # more}}"
              description={{
                text: "Show grid items that are more than certain number of rows that was previously made hidden",
                img: "https://cdn.faire.com/fastly/43888c804cb4a00e94c69bdb9293f0276fcb33143979e2580d4b8e4cc726b113.png",
              }}
              values={{
                tileNumber: numHiddenTiles,
              }}
            />
            <Arrow direction="down" width="16px" height="16px" />
          </>
        )}
      </Button>
    </SDUIOnClickHandler>
  );
};
